import { ServiceTypes } from 'api/types/ReferenceData';
import RowSelector from 'components/common/RowSelector/RowSelector';
import { Spinner } from 'components/common/Spinner/Spinner';
import { getFoodItemInfo, getItemInfo } from 'helpers/Search';
import { useComponentSize } from 'hooks/useComponentSize';
import useOnScreen from 'hooks/useOnScreen';
import { useAuth } from 'react-oidc-context';

import { useEffect, useRef, useState } from 'react';
import {
  Col,
  ListGroup,
  ListGroupItem,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SimpleBarReact from 'simplebar-react';
import { ProductType } from 'types/Products';
import { SearchDocument, SearchResult, SearchResults } from 'types/Search';

import { Bookmark } from '../../Bookmark/Bookmark';

import OpenInNew from '@mui/icons-material/OpenInNew';
import classNames from 'classnames';
import { BrandPortalStatusTag } from 'components/common/BrandPortalStatusTag';
import CompanyLabelDot from 'components/common/Events/CompanyLabelDot';
import SeparatorLine, {
  SeparatorVariants
} from 'components/common/SeparatorLine';
import { VerifiedCompanyIcon } from 'components/common/icon/VerifiedCompanyIcon';
import { doesCompanyRejectInquiries } from 'components/pages/products/inquiryRejection';
import { useFlags } from 'launchdarkly-react-client-sdk';
import piwik from 'piwik/piwik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { tss } from 'tss-react/mui';
import { SegmentTag } from 'types/global';
import { MarketingBanner } from '../MarketingBanner';
import SearchAward from '../SearchCertificationList/SearchAwards';
import SearchCertificationList from '../SearchCertificationList/SearchCertificationList';
import { SignInBanner } from '../SignInDialogs/SignInBanner';

interface SearchResultListProps {
  target: ProductType;
  searchResponse: SearchResults;
  selectionModeEnabled: boolean;
  requestsModeActive: boolean;
  selectedItemList: SearchDocument[];
  onCompareRequestModeRowClick: (newCompareItemList: SearchDocument[]) => void;
  logItemClick(document: SearchDocument): void;
  getMoreResults: () => void;
  searchRequest: any;
  bulkItemsLimit: number;
  isBrandPortal: boolean | undefined;
  setShowRegisterModal: (a: boolean) => void;
}

const SearchResultList = ({
  target,
  searchResponse,
  selectionModeEnabled,
  requestsModeActive,
  selectedItemList,
  onCompareRequestModeRowClick,
  logItemClick,
  getMoreResults,
  bulkItemsLimit,
  isBrandPortal,
  setShowRegisterModal
}: SearchResultListProps) => {
  const { isAuthenticated } = useAuth();
  const [selectedRowDetails, setSelectedRowDetails] =
    useState<SearchDocument | null>(null);
  const [showBackToTopButton, setShowBackToTopButton] = useState(false);
  const scrollRef = useRef<HTMLElement>(null);
  const refTable = useRef<HTMLDivElement | null>(null);
  const { width } = useComponentSize(refTable);
  const searchResultListSpinnerRef = useRef<HTMLElement>(null);
  const isVisible = useOnScreen({ ref: searchResultListSpinnerRef });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { event: eventKey } = useParams();
  const { searchMarketingBannerV2 } = useFlags();

  const { classes } = useStyles();

  if (!target) {
    navigate('/ingredients/search');
  }
  const segment = searchResponse?.search?.segment;
  const isInCosmeticsBranding =
    segment === SegmentTag.PERSONALCARE && target === ProductType.INGREDIENTS;
  const tableBreakpoints = {
    XXS: 420,
    MOBILE: isBrandPortal ? 710 : 600,
    SM: isBrandPortal ? 870 : 650,
    MD: isBrandPortal ? 1135 : 700,
    LG: isBrandPortal ? 1300 : 1012
  };
  const tableSize =
    width < tableBreakpoints.XXS
      ? 'XXS'
      : width < tableBreakpoints.MOBILE
      ? 'MOBILE'
      : width < tableBreakpoints.SM
      ? 'SM'
      : width < tableBreakpoints.MD
      ? 'MD'
      : 'LG';
  const mobileBreakpoint = tableBreakpoints.MOBILE;
  const atttributesBreakpoint = tableBreakpoints.SM;
  const certificationsBreakpoint = isBrandPortal ? 1135 : 700;
  useEffect(() => {
    setSelectedRowDetails(null);
  }, [selectionModeEnabled]);

  useEffect(() => {
    if (isVisible) {
      searchResultListSpinnerRef?.current !== null && getMoreResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);
  useEffect(() => {
    const handleClick = () => {
      if (scrollRef !== null && scrollRef.current !== null) {
        if (
          scrollRef.current.scrollTop >= 150 &&
          showBackToTopButton === false
        ) {
          setShowBackToTopButton(true);
        } else if (scrollRef.current.scrollTop < 150) {
          setShowBackToTopButton(false);
        }
      }
    };

    if (scrollRef !== null && scrollRef.current !== null) {
      const element = scrollRef.current;

      element.addEventListener('scroll', handleClick);

      return () => {
        element.removeEventListener('scroll', handleClick);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onItemClickHandler = (document: SearchDocument, activeRow: boolean) => {
    if (
      (selectionModeEnabled && selectedItemList.length < bulkItemsLimit) ||
      activeRow
    ) {
      const newSelectedItemList: SearchDocument[] = selectedItemList.slice();
      const selectedItemListIndex: number = selectedItemList.findIndex(
        item => item.id === document.id
      );

      if (selectedItemListIndex === -1) {
        piwik.push([
          'trackEvent',
          'Compare',
          'compare-document',
          document.name
        ]);
        newSelectedItemList.push(document);
      } else {
        newSelectedItemList.splice(selectedItemListIndex, 1);
      }

      onCompareRequestModeRowClick(newSelectedItemList);
    } else {
      //selection mode enabled is off
      const selectedRow =
        selectedRowDetails?.id === document.id ? null : document;

      if (selectedRow) {
        piwik.push([
          'trackEvent',
          'Search',
          'openedProductPageFromRowClick',
          selectedRow.name,
          selectedRow.key
        ]);
      }

      logItemClick(document);
    }
  };

  const checkIfActiveRow = (documentId: string) => {
    const compareListIndex: number = selectedItemList.findIndex(
      item => item.id === documentId
    );
    if (compareListIndex === -1) {
      return false;
    } else {
      return true;
    }
  };

  const getItemRowClasses = (documentId: string, activeRow: boolean) => {
    const baseClass = 'search-result-list-group-item-';
    if (selectionModeEnabled && activeRow) {
      return baseClass + 'compare-selected';
    } else if (selectionModeEnabled) {
      return baseClass + 'compare-active';
    } else if (selectedRowDetails?.id === documentId) {
      return baseClass + 'selected';
    } else {
      return '';
    }
  };

  const scrollToTop = () => {
    if (scrollRef !== null && scrollRef.current !== null) {
      scrollRef.current.scrollTop = 0;
    }
  };

  const getProducePageLink = (productKey: string) =>
    !eventKey
      ? `/${target.toLowerCase()}/permalink/${productKey}`
      : `/events/${eventKey}/product/${productKey}`;

  return (
    <>
      {searchResponse?.results?.length > 0 ? (
        <div
          className={`search-result-list type-${target}`}
          style={{
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          <SimpleBarReact
            className="testing"
            scrollableNodeProps={{ id: 'scroll-node', ref: scrollRef }}
            style={{ minHeight: 0 }}
          >
            {width >= 400 && (
              <div
                className={classNames(
                  'search-results-table-header',
                  'table-responsive',
                  `search-results-table-header-${tableSize}`,
                  {
                    [`search-results-table-header-events-${tableSize}`]:
                      isBrandPortal && target === ProductType.INGREDIENTS
                  },
                  {
                    'search-results-table-header-food':
                      segment === SegmentTag.FOOD
                  }
                )}
              >
                {/* //DONT REMOVE EMPTY P-TAG */}
                <p></p>

                {width >= mobileBreakpoint &&
                  isBrandPortal &&
                  target === ProductType.INGREDIENTS && <p>NART</p>}
                <p>{t(`reference.productTypes.${target}`)}</p>
                {width >= atttributesBreakpoint && <p>Attributes</p>}
                <p>Company</p>

                {width >= mobileBreakpoint &&
                  isBrandPortal &&
                  target === ProductType.INGREDIENTS && <p>Status</p>}
                {width >= certificationsBreakpoint &&
                  target === ProductType.INGREDIENTS &&
                  segment !== SegmentTag.FOOD && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          {isInCosmeticsBranding === undefined
                            ? 'Certifications'
                            : 'Awards & Certifications'}
                        </Tooltip>
                      }
                    >
                      <p className="text-start">
                        {isInCosmeticsBranding === undefined
                          ? 'Certifications'
                          : 'Awards & Certifications'}
                      </p>
                    </OverlayTrigger>
                  )}
              </div>
            )}

            <div ref={refTable}>
              <InfiniteScroll
                scrollableTarget="scroll-node"
                dataLength={searchResponse?.results?.length}
                next={() => {
                  //   getMoreResults();
                }}
                hasMore={
                  searchResponse?.total > searchResponse?.results?.length + 1
                }
                loader={
                  <Spinner
                    reference={searchResultListSpinnerRef}
                    key="search-result-list-spinner"
                    className="search-result-list-spinner"
                  />
                }
                scrollThreshold={0.8}
              >
                <ListGroup>
                  {searchResponse?.results?.map(
                    (result: SearchResult, index: number) => {
                      const activeRow: boolean = checkIfActiveRow(
                        result?.document?.id
                      );
                      let documentName =
                        result?.document?.type === ProductType.SERVICES
                          ? ServiceTypes.find(
                              service =>
                                service.value === result?.document?.name
                            )?.label
                          : result.document.name;
                      if (documentName === undefined) {
                        documentName = result.document.name;
                      }

                      const canMakeInquiries = !(
                        requestsModeActive &&
                        doesCompanyRejectInquiries(result.document.company.key)
                      );

                      const isBannerIndex =
                        ((index + 1) % 12 === 0 && index !== 0) ||
                        (searchResponse?.results.length < 12 &&
                          searchResponse?.results.length === index + 1);

                      return (
                        <React.Fragment key={result.document.id}>
                          <Link
                            to={getProducePageLink(result.document.old_key)}
                            onClick={e =>
                              selectionModeEnabled && e.preventDefault()
                            }
                          >
                            <ListGroupItem
                              key={result.document.id}
                              className={`search-result-list-group-item ${getItemRowClasses(
                                result.document.id,
                                activeRow
                              )}`}
                              style={{
                                cursor: canMakeInquiries
                                  ? 'pointer'
                                  : 'not-allowed'
                              }}
                              onClick={() => {
                                if (canMakeInquiries)
                                  onItemClickHandler(
                                    result.document,
                                    activeRow
                                  );
                              }}
                            >
                              <article className="search-result-list-item-article">
                                <div
                                  className={classNames(
                                    'search-result-list-item-row',
                                    `search-result-list-item-row-${tableSize}`,
                                    {
                                      [`search-result-list-item-row-events-${tableSize}`]:
                                        isBrandPortal &&
                                        target === ProductType.INGREDIENTS
                                    },
                                    {
                                      'search-result-list-item-row-food':
                                        segment === SegmentTag.FOOD
                                    }
                                  )}
                                >
                                  <Col xs={0.5} style={{ margin: 'auto' }}>
                                    {(selectionModeEnabled &&
                                      selectedItemList.length <
                                        bulkItemsLimit) ||
                                    (selectionModeEnabled && activeRow) ? (
                                      <RowSelector
                                        active={activeRow}
                                        disabled={!canMakeInquiries}
                                      />
                                    ) : (
                                      <>
                                        {isAuthenticated && (
                                          <Bookmark
                                            product={result.document}
                                            isBookmarked={
                                              !!result.document?.bookmarked
                                            }
                                          />
                                        )}
                                      </>
                                    )}
                                  </Col>
                                  {width >= mobileBreakpoint &&
                                    isBrandPortal &&
                                    target === ProductType.INGREDIENTS && (
                                      <Col className="search-result-list-nart-number">
                                        {
                                          result.document?.event
                                            ?.product_private_attributes
                                            ?.internal_id
                                        }
                                      </Col>
                                    )}
                                  <Col
                                    className="search-result-list-name truncate"
                                    dangerouslySetInnerHTML={{
                                      __html: documentName
                                    }}
                                  />
                                  {width >= atttributesBreakpoint && (
                                    <Col className="search-result-list-info">
                                      {segment !== SegmentTag.FOOD
                                        ? getItemInfo(result?.document)
                                        : getFoodItemInfo(result?.document)}
                                    </Col>
                                  )}
                                  <Col className="search-result-list-company">
                                    <span className="search-result-list-company-container">
                                      <Link
                                        className="search-result-list-company-name d-flex align-items-center"
                                        to={
                                          //TODO:
                                          //Not sure if this is enough or we need to check if he is a member
                                          //need to check access page for user who gets link but is not member
                                          !eventKey
                                            ? `/company/${result?.document?.company?.key}`
                                            : `/events/${eventKey}/company/${result?.document?.company?.key}`
                                        }
                                      >
                                        {result?.document?.event
                                          ?.company_private_attributes
                                          ?.label && (
                                          <CompanyLabelDot
                                            company_attributes={
                                              result?.document?.event
                                                ?.company_private_attributes
                                                ?.label
                                            }
                                          />
                                        )}
                                        {result?.document?.company?.name}
                                        <span className="ms-1" />
                                        {result?.document?.company
                                          ?.verified && (
                                          <div
                                            style={{ transform: 'scale(.8)' }}
                                          >
                                            <VerifiedCompanyIcon />
                                          </div>
                                        )}
                                      </Link>
                                      {/* {result?.document?.company?.verified && (
                                <Tag tooltipText="Verified supplier">
                                  Verified
                                </Tag>
                              )} */}
                                    </span>
                                  </Col>
                                  {isBrandPortal &&
                                    width >= mobileBreakpoint &&
                                    target === ProductType.INGREDIENTS && (
                                      <Col className="search-result-list-status">
                                        {result?.document?.event
                                          ?.product_private_attributes
                                          ?.label && (
                                          <BrandPortalStatusTag
                                            tooltip
                                            label={
                                              result?.document?.event
                                                ?.product_private_attributes
                                                ?.label
                                            }
                                          />
                                        )}
                                      </Col>
                                    )}
                                  {width >= certificationsBreakpoint &&
                                    target === ProductType.INGREDIENTS &&
                                    segment !== SegmentTag.FOOD && (
                                      <Col className="search-result-list-certification d-flex">
                                        {result?.document?.awards && (
                                          <SearchAward
                                            awards={result?.document?.awards}
                                          />
                                        )}

                                        {result?.document?.awards &&
                                          result?.document?.categories
                                            ?.technical?.certifications && (
                                            <SeparatorLine
                                              variant={
                                                SeparatorVariants.VERTICAL
                                              }
                                              style={{ height: '30px' }}
                                            />
                                          )}

                                        <SearchCertificationList
                                          certifications={
                                            result?.document?.categories
                                              ?.technical?.certifications
                                          }
                                        />
                                      </Col>
                                    )}
                                  <div className="table-action-btn-wrapper">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip>Open product page</Tooltip>
                                      }
                                    >
                                      <button
                                        aria-label="Open product page"
                                        disabled={selectionModeEnabled}
                                        className={`btn btn-falcon-primary search-compare-btn table-action-btn`}
                                        style={{
                                          opacity: selectionModeEnabled
                                            ? 0.1
                                            : 1,
                                          transition: '.3s ease all'
                                        }}
                                      >
                                        <OpenInNew
                                          sx={{ ml: -1 }}
                                          onClick={e => {
                                            if (selectionModeEnabled) return;
                                            e.stopPropagation();
                                            e.preventDefault();
                                            logItemClick(result.document);
                                            piwik.push([
                                              'trackEvent',
                                              'Search',
                                              'openedProductPageFromIconClick',
                                              result.document.name,
                                              result.document.key
                                            ]);
                                            navigate(
                                              getProducePageLink(
                                                result.document.old_key
                                              )
                                            );
                                          }}
                                        />
                                      </button>
                                    </OverlayTrigger>
                                  </div>
                                </div>
                              </article>
                            </ListGroupItem>
                          </Link>
                          {isBannerIndex &&
                            (!isAuthenticated ? (
                              <SignInBanner
                                position="list"
                                setShowRegisterModal={setShowRegisterModal}
                                segment={
                                  searchResponse.search?.segment ??
                                  SegmentTag.PERSONALCARE
                                }
                              />
                            ) : searchMarketingBannerV2?.active ? (
                              <MarketingBanner {...searchMarketingBannerV2} />
                            ) : null)}
                        </React.Fragment>
                      );
                    }
                  )}
                </ListGroup>
              </InfiniteScroll>
            </div>
          </SimpleBarReact>

          {showBackToTopButton && (
            <div className="search-result-list-floating-button-group">
              <button onClick={scrollToTop} className={classes.backToTopButton}>
                <svg width="16" height="20" viewBox="4 2 16 20">
                  <path d="M4,14.0002003l1.4099998,1.4099998L11,9.8302002v12.1700001h2V9.8302002l5.5799999,5.5900002L20,14.0002003l-8-8 L4,14.0002003z M4,1.8752h16v2H4V1.8752z" />
                </svg>
                Back to top
              </button>
            </div>
          )}
        </div>
      ) : (
        <span>No matching {target.toLocaleLowerCase()}</span>
      )}
    </>
  );
};

const useStyles = tss.create(({ theme }) => ({
  backToTopButton: {
    padding: '10px 15px',
    minHeight: '42px',
    minWidth: '100px',
    color: '#fff',
    fontWeight: '500 !important',
    fontSize: '14px !important',
    backgroundColor: `${theme.palette.primary.main}80`,
    fill: '#fff',
    border: 'none',
    margin: '1px',
    outline: 'none',
    zIndex: 99,
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    svg: { marginRight: '10px' },
    '&:hover': { svg: { color: '#fff !important' } }
  }
}));

export default SearchResultList;
